/**
 * Media kit accent palette — the single source of truth for the accent
 * theming system. Server-safe (no client-only imports) so server actions,
 * share pages and the client picker all share one definition.
 *
 * The chosen accent overrides --brand-rgb / --brand-2-rgb (styles/main.css),
 * which re-themes every brand-coloured element site-wide. Each accent ships a
 * companion colour used as the far stop of two-tone gradients.
 */

export type KitAccent = {
  name: string;
  hex: string;
  /** Space-separated RGB channels of `hex`, for rgb(var() / alpha) usage. */
  rgb: string;
  /** Companion gradient colour channels (replaces the default purple). */
  companionRgb: string;
  /**
   * Legible foreground for text/icons on a solid `bg-brand` fill (drives
   * --brand-fg). Preserve the default white-on-pink brand treatment; use dark
   * ink for the lighter custom accents. White-on-pink clears 3:1, not 4.5:1.
   */
  fg: string;
};

const DARK_INK = '#0a0a0a';

export const DEFAULT_KIT_ACCENT: KitAccent = {
  name: 'Pink',
  hex: '#ec4899',
  rgb: '236 72 153',
  companionRgb: '139 92 246',
  fg: '#ffffff',
};

export const KIT_ACCENTS: KitAccent[] = [
  DEFAULT_KIT_ACCENT,
  { name: 'Violet', hex: '#a78bfa', rgb: '167 139 250', companionRgb: '217 70 239', fg: DARK_INK },
  { name: 'Sky', hex: '#38bdf8', rgb: '56 189 248', companionRgb: '99 102 241', fg: DARK_INK },
  { name: 'Emerald', hex: '#34d399', rgb: '52 211 153', companionRgb: '34 211 238', fg: DARK_INK },
  { name: 'Amber', hex: '#fbbf24', rgb: '251 191 36', companionRgb: '251 146 60', fg: DARK_INK },
  { name: 'Ice', hex: '#e2e8f0', rgb: '226 232 240', companionRgb: '148 163 184', fg: DARK_INK },
];

export function findKitAccentByHex(hex: string | null | undefined): KitAccent | null {
  if (!hex) return null;
  return KIT_ACCENTS.find((accent) => accent.hex === hex) ?? null;
}

/**
 * CSS for a server-rendered `<style>` override tag. Only ever emits values
 * from the palette — unknown input returns null, so user-supplied strings can
 * never reach the stylesheet.
 */
export function buildAccentCssOverride(hex: string): string | null {
  const accent = findKitAccentByHex(hex);
  if (!accent) return null;
  return `:root{--brand-rgb:${accent.rgb};--brand-2-rgb:${accent.companionRgb};--brand-fg:${accent.fg};}`;
}

export type AccentReconcileDecision =
  { action: 'pull'; hex: string } | { action: 'push'; hex: string } | { action: 'none' };

/**
 * Decides how to sync the profile accent (server) with the localStorage
 * cache (client) when a logged-in user loads their dashboard:
 * - server has a value → it wins; pull it into the local cache
 * - server empty but the user picked locally (e.g. on the demo before
 *   signing up) → push the local choice to the server
 * - otherwise nothing to do. Invalid values are treated as unset.
 */
export function reconcileAccent({
  serverHex,
  localHex,
}: {
  serverHex: string | null;
  localHex: string | null;
}): AccentReconcileDecision {
  const server = findKitAccentByHex(serverHex);
  const local = findKitAccentByHex(localHex);

  if (server) {
    return server.hex === local?.hex ? { action: 'none' } : { action: 'pull', hex: server.hex };
  }
  if (local) {
    return { action: 'push', hex: local.hex };
  }
  return { action: 'none' };
}
